/** Support tickets clients' AI escalated over MCP (client MCP plan §5.4.1, Q5; backend WPS P1-11):
 *  support's side of the `escalate` tool.
 *
 *  - `GET /support_tickets/` (`console_support_tickets_view`) — the grid: every tenant's tickets,
 *    newest first, page-numbered, under the statement-timeout guard. One row is our facts only:
 *    the title is ours, never the client's.
 *  - `GET /support_tickets/{public_id}/` — the ticket page: the case file, the client's words in
 *    ONE block marked untrusted (`client_text`), the bookings, the internal diagnose, every
 *    delivery and the status history.
 *  - `POST /support_tickets/{public_id}/status/` `{status, reason}` (also
 *    `console_support_tickets_manage`) — forward only; a move that is no longer forward (the ticket
 *    moved meanwhile) is a 409 `status_refused`. Audited in the console as
 *    `support_ticket.status_changed` with the reason; the client's Activity shows the two statuses,
 *    never the reason or who moved it.
 *
 *  Shapes come from the generated spec types; this module adds the query keys and the words. */
import { apiGet, apiPost, ConsoleApiError, extendedListConfig } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

type Schemas = components['schemas'];

export type SupportTicket = Schemas['ConsoleSupportTicket'];
export type SupportTicketDetail = Schemas['ConsoleSupportTicketDetail'];
export type SupportTicketStatus = Schemas['ConsoleSupportTicketStatus'];
export type SupportTicketSeverity = Schemas['ConsoleSupportTicketSeverity'];
export type SupportTicketDeliveryStatus = Schemas['ConsoleSupportTicketDeliveryStatus'];
export type SupportTicketSinkStatus = Schemas['ConsoleSupportTicketSinkStatus'];
export type SupportTicketDelivery = Schemas['ConsoleSupportTicketDelivery'];
export type SupportTicketFollowup = Schemas['ConsoleSupportTicketFollowup'];
export type SupportTicketStatusChange = Schemas['ConsoleSupportTicketStatusChange'];
export type SupportTicketError = Schemas['ConsoleSupportTicketError'];
export type SupportTicketClientText = SupportTicketDetail['client_text'];
export type SupportTicketCase = NonNullable<SupportTicketDetail['case']>;

/** The longest reason the status change takes. */
export const SUPPORT_TICKET_REASON_MAX = 500;

/** The console audit log's event for a status change. */
export const SUPPORT_TICKET_STATUS_AUDIT_EVENT = 'support_ticket.status_changed';

// ---- list ------------------------------------------------------------------

/** The grid's filters, by their API names (the page's `useGridFilters` defs resolve to these). */
export interface SupportTicketFilters {
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  /** One status or a comma-separated list. */
  status?: string;
  /** One severity or a comma-separated list. */
  severity?: string;
  /** The tickets with any delivery in this status (one or a comma-separated list). */
  delivery_status?: string;
  created_after?: string;
  created_before?: string;
  /** The tickets whose case covers this booking. */
  booking_id?: number;
  /** A ticket id as people quote it, a part of one, or words of our title. */
  search?: string;
}

export interface SupportTicketListParams extends SupportTicketFilters {
  ordering?: string;
  timeout?: 'extended';
  page?: number;
  page_size?: number;
}

type QueryValue = string | number | boolean | null | undefined;

function toQuery(params: Record<string, QueryValue>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

const path = (publicId: string) => `/support_tickets/${encodeURIComponent(publicId)}/`;

export function list(
  params: SupportTicketListParams = {},
): Promise<PaginatedResponse<SupportTicket>> {
  return apiGet(`/support_tickets/${toQuery({ ...params })}`, extendedListConfig(params.timeout));
}

export function retrieve(publicId: string): Promise<SupportTicketDetail> {
  return apiGet(path(publicId));
}

/** Moves the ticket on and answers it. A move that is not forward (any more) is a
 *  `ConsoleApiError` 409 whose body `statusRefusedOf` reads. */
export function changeStatus(
  publicId: string,
  body: SupportTicketStatusChange,
): Promise<SupportTicketDetail> {
  return apiPost(`${path(publicId)}status/`, body);
}

/** The typed body of a 409 `status_refused`, or null for any other failure. */
export function statusRefusedOf(e: unknown): SupportTicketError | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 409) return null;
  const body = e.body as Partial<SupportTicketError> | undefined;
  return body?.code === 'status_refused' ? (body as SupportTicketError) : null;
}

/** A readable message from a refused status change: the 409's `detail`, or DRF's field-keyed
 *  `{status|reason: ["…"]}`. */
export function statusChangeErrorText(e: unknown, fallback = 'The status change failed.'): string {
  if (!(e instanceof ConsoleApiError)) return e instanceof Error ? e.message : fallback;
  const body = e.body as Record<string, unknown> | undefined;
  if (typeof body?.detail === 'string' && body.detail) return body.detail;
  for (const field of ['status', 'reason']) {
    const v = body?.[field];
    if (typeof v === 'string' && v) return v;
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  }
  return e.message || fallback;
}

/** Everything under `supportTicketKeys.all`, so a status change refreshes the grid and the page. */
export const supportTicketKeys = {
  all: ['supportTickets'] as const,
  list: ['supportTickets', 'list'] as const,
  detail: (publicId: string) => ['supportTickets', 'detail', publicId] as const,
};

// ---- words -------------------------------------------------------------------

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

interface Word<V extends string> {
  value: V;
  label: string;
  color: ChipColor;
}

/** In their order: forward only, a step may be skipped. */
export const TICKET_STATUSES: readonly Word<SupportTicketStatus>[] = [
  { value: 'open', label: 'Open', color: 'warning' },
  { value: 'acknowledged', label: 'Acknowledged', color: 'info' },
  { value: 'resolved', label: 'Resolved', color: 'success' },
  { value: 'closed', label: 'Closed', color: 'default' },
];

/** Low → urgent. */
export const TICKET_SEVERITIES: readonly Word<SupportTicketSeverity>[] = [
  { value: 'low', label: 'Low', color: 'default' },
  { value: 'normal', label: 'Normal', color: 'info' },
  { value: 'urgent', label: 'Urgent', color: 'error' },
];

export const DELIVERY_STATUSES: readonly Word<SupportTicketDeliveryStatus>[] = [
  { value: 'pending', label: 'Pending', color: 'info' },
  { value: 'sent', label: 'Sent', color: 'success' },
  { value: 'skipped', label: 'Skipped', color: 'default' },
  { value: 'failed', label: 'Failed', color: 'error' },
];

/** The subject a ticket is about: one open ticket per booking, order or guest. */
export const SUBJECT_SCOPE_LABELS: Record<SupportTicket['subject']['scope'], string> = {
  booking: 'Booking',
  order: 'Order',
  guest: 'Guest',
};

export const DIAGNOSE_STATE_LABELS: Record<SupportTicket['diagnose_state'], string> = {
  started: 'Started',
  skipped: 'Skipped',
  none: 'None',
};

const wordOf = <V extends string>(words: readonly Word<V>[], value: string) =>
  words.find((w) => w.value === value);

export const statusLabel = (v: string) => wordOf(TICKET_STATUSES, v)?.label ?? v;
export const statusColor = (v: string): ChipColor => wordOf(TICKET_STATUSES, v)?.color ?? 'default';
export const severityLabel = (v: string) => wordOf(TICKET_SEVERITIES, v)?.label ?? v;
export const severityColor = (v: string): ChipColor =>
  wordOf(TICKET_SEVERITIES, v)?.color ?? 'default';
export const deliveryStatusLabel = (v: string) => wordOf(DELIVERY_STATUSES, v)?.label ?? v;
export const deliveryStatusColor = (v: string): ChipColor =>
  wordOf(DELIVERY_STATUSES, v)?.color ?? 'default';

/** "Open" and "Acknowledged": the subject's one open ticket. */
export const OPEN_STATUSES: readonly SupportTicketStatus[] = ['open', 'acknowledged'];
